const COLLATOR = new Intl.Collator("en", { sensitivity: "base", numeric: true });

export const CONVERSATION_SORT = {
  NEWEST: "newest",
  OLDEST: "oldest",
};

export const RELATIONSHIP_SORT = {
  NAME_ASC: "name-asc",
  NAME_DESC: "name-desc",
  SCORE_DESC: "score-desc",
  SCORE_ASC: "score-asc",
  LATEST_DESC: "latest-desc",
  LATEST_ASC: "latest-asc",
};

export function conversationOutcome(call) {
  return String(call?.conversation_status?.outcome || "").trim();
}

function timestampValue(value) {
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function conversationDateValue(call) {
  return timestampValue(call?.occurred_at || call?.created_at);
}

export function sortConversationCalls(calls = [], order = CONVERSATION_SORT.NEWEST) {
  const direction = order === CONVERSATION_SORT.OLDEST ? 1 : -1;

  return calls
    .map((call, index) => ({ call, index, date: conversationDateValue(call) }))
    .sort((left, right) => {
      if (left.date === null && right.date === null) return left.index - right.index;
      if (left.date === null) return 1;
      if (right.date === null) return -1;
      const byDate = (left.date - right.date) * direction;
      return byDate || left.index - right.index;
    })
    .map(({ call }) => call);
}

function relationshipName(customer) {
  return String(customer?.name || customer?.company || "").trim();
}

function scoreValue(customer) {
  const rawValue = customer?.relationship_score;
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : null;
}

function latestCallValue(customer) {
  return timestampValue(customer?.last_call_at);
}

function compareNullableNumbers(left, right, direction) {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return (left - right) * direction;
}

function compareNames(left, right, direction = 1) {
  const byName = COLLATOR.compare(relationshipName(left), relationshipName(right)) * direction;
  if (byName) return byName;
  return COLLATOR.compare(String(left?.key || ""), String(right?.key || "")) * direction;
}

export function isAlphabeticalRelationshipSort(mode) {
  return mode === RELATIONSHIP_SORT.NAME_ASC || mode === RELATIONSHIP_SORT.NAME_DESC;
}

export function sortRelationshipCustomers(customers = [], mode = RELATIONSHIP_SORT.NAME_ASC) {
  return customers
    .map((customer, index) => ({ customer, index }))
    .sort((leftEntry, rightEntry) => {
      const left = leftEntry.customer;
      const right = rightEntry.customer;
      let comparison = 0;

      switch (mode) {
        case RELATIONSHIP_SORT.NAME_DESC:
          comparison = compareNames(left, right, -1);
          break;
        case RELATIONSHIP_SORT.SCORE_DESC:
          comparison = compareNullableNumbers(scoreValue(left), scoreValue(right), -1);
          break;
        case RELATIONSHIP_SORT.SCORE_ASC:
          comparison = compareNullableNumbers(scoreValue(left), scoreValue(right), 1);
          break;
        case RELATIONSHIP_SORT.LATEST_DESC:
          comparison = compareNullableNumbers(latestCallValue(left), latestCallValue(right), -1);
          break;
        case RELATIONSHIP_SORT.LATEST_ASC:
          comparison = compareNullableNumbers(latestCallValue(left), latestCallValue(right), 1);
          break;
        case RELATIONSHIP_SORT.NAME_ASC:
        default:
          comparison = compareNames(left, right, 1);
          break;
      }

      if (comparison) return comparison;
      const nameFallback = compareNames(left, right, 1);
      return nameFallback || leftEntry.index - rightEntry.index;
    })
    .map(({ customer }) => customer);
}
